import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useNavigation } from '../../../../hooks/useNavigation';
import { useAuth } from '../../../../contexts/AuthContext';
import { useAlert } from '../../../../contexts/AlertContext';
import { estimateApi, jobApi, careApi } from '../../../../services/api';
import TopNavigation from '../../../../components/feature/TopNavigation';
import Card from '../../../../components/base/Card';
import Button from '../../../../components/base/Button';

const MIN_HOURLY_WAGE = 10320; // 2026년 최저시급



export default function CaregiverJobApply() {
    const navigate = useNavigate();
    const { navigateTo, goBack } = useNavigation();
    const location = useLocation();


    const { jobId } = useParams();
    const { user } = useAuth();
    const { showAlert, hideAlert } = useAlert();

    const isFamilyCare = location.state?.isFamilyCare || false;

    const [proposedRate, setProposedRate] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [careDetail, setCareDetail] = useState<any>(null);
    const [jobDetail, setJobDetail] = useState<any>(null);
    const fromSentQuotes = location.state?.fromSentQuotes || false;
    const estimateNoFromState = location.state?.estimateNo;
    const oneDayMoneyFromState = location.state?.oneDayMoney;
    const careWhatFromState = location.state?.careWhat;
    const [existingEstimate, setExistingEstimate] = useState<any>(null);

    const rawState = location.state?.state || location.state || {};
    const hasEstimateFromState = !!(rawState.estimateNo);
    const [isEditMode, setIsEditMode] = useState(hasEstimateFromState);
    const isEditModeRef = useRef(hasEstimateFromState);
    const existingEstimateRef = useRef<any>(
        hasEstimateFromState ? {
            estimateNo: rawState.estimateNo,
            oneDayMoney: rawState.oneDayMoney || 0
        } : null
    );

    const storageKey = `apply_${jobId}`;

    const getCurrentUser = () => {
        try {
            const userData = localStorage.getItem('user');
            if (userData) return JSON.parse(userData);
        } catch (error) {
            console.error('[Apply] localStorage 파싱 에러:', error);
        }
        return user;
    };

    useEffect(() => {
        if (jobId && user) {
            restoreFromStorage();

            const isBackFromPayment = sessionStorage.getItem(`backFromPayment_${jobId}`);
            if (isBackFromPayment) {
                console.log('[Apply] 결제 페이지에서 복귀');
                sessionStorage.removeItem(`backFromPayment_${jobId}`);
            }

            const timer = setTimeout(() => {
                loadInitialData();
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [jobId, user]);

    useEffect(() => {
        if (proposedRate || message) {
            saveToStorage();
        }
    }, [proposedRate, message]);

    const restoreFromStorage = () => {
        // ✅ 추가 - state에 estimateNo가 있으면 복원 건너뜀 (새 값으로 덮어씌워질 것)
        const rawState = location.state?.state || location.state || {};
        if (rawState.estimateNo) {
            console.log('[Apply] 수정 모드 진입 - sessionStorage 복원 건너뜀');
            sessionStorage.removeItem(storageKey);
            return;
        }

        try {
            const stored = sessionStorage.getItem(storageKey);
            if (stored) {
                const data = JSON.parse(stored);
                if (data.proposedRate) setProposedRate(data.proposedRate);
                if (data.message) setMessage(data.message);
                if (data.existingEstimate) {
                    setExistingEstimate(data.existingEstimate);
                    setIsEditMode(true);
                    isEditModeRef.current = true;
                    existingEstimateRef.current = data.existingEstimate;
                }
                console.log('[Apply] sessionStorage 복원:', data);
            }
        } catch (error) {
            console.error('[Apply] sessionStorage 복원 실패:', error);
        }
    };

    const saveToStorage = () => {
        try {
            const data = {
                proposedRate,
                message,
                existingEstimate,
                careWhat: isFamilyCare ? 2 : (jobDetail?.careWhat || 1),
                timestamp: Date.now()
            };
            sessionStorage.setItem(storageKey, JSON.stringify(data));
        } catch (error) {
            console.error('[Apply] sessionStorage 저장 실패:', error);
        }
    };

    const setEditMode = (estimate: any) => {
        setExistingEstimate(estimate);
        setIsEditMode(true);
        isEditModeRef.current = true;
        existingEstimateRef.current = estimate;
    };

    const loadInitialData = async () => {
        const currentUser = getCurrentUser();
        if (!currentUser) return;

        setIsLoading(true);

        try {
            if (isFamilyCare) {
                try {
                    const detail = await careApi.getRequestDetail({
                        memberNo: currentUser.memberNo,
                        apiKey: currentUser.apiKey,
                        careNo: parseInt(jobId!)
                    });
                    setCareDetail(detail);
                    console.log('[Apply] 가족간병 상세:', detail);

                    const stored = sessionStorage.getItem(storageKey);
                    const storedData = stored ? JSON.parse(stored) : null;

                    if (!storedData?.existingEstimate && estimateNoFromState) {
                        const existing = {
                            estimateNo: estimateNoFromState,
                            oneDayMoney: oneDayMoneyFromState || 0
                        };
                        setEditMode(existing);

                        if (oneDayMoneyFromState) {
                            setProposedRate(oneDayMoneyFromState.toLocaleString());
                        }

                        const newStoredData = {
                            ...(storedData || {}),
                            existingEstimate: existing,
                            careWhat: 2,
                            timestamp: Date.now()
                        };
                        sessionStorage.setItem(storageKey, JSON.stringify(newStoredData));
                    }
                } catch (error) {
                    console.error('[Apply] 가족간병 상세 조회 실패:', error);
                }

            } else {
                try {
                    const detail = await jobApi.getDetail({
                        memberNo: currentUser.memberNo,
                        apiKey: currentUser.apiKey,
                        careNo: parseInt(jobId!)
                    });

                    setJobDetail(detail);
                    console.log('[Apply] 공고 상세 정보:', detail);

                    if (fromSentQuotes && estimateNoFromState) {
                        // 보낸견적에서 온 경우
                        const existing = {
                            estimateNo: estimateNoFromState,
                            oneDayMoney: oneDayMoneyFromState || 0
                        };
                        setEditMode(existing);

                        // ✅ API 응답값 우선 사용
                        if (detail.oneDayMoney > 0) {
                            setProposedRate(detail.oneDayMoney.toLocaleString());
                        } else if (oneDayMoneyFromState) {
                            setProposedRate(oneDayMoneyFromState.toLocaleString());
                        } else if (detail.matchType === 'F') {
                            setProposedRate(detail.oneDayMoney.toLocaleString());
                        }

                        // ✅ 추가
                        if (detail.message) {
                            setMessage(detail.message);
                        }

                    } else if (!fromSentQuotes && estimateNoFromState) {
                        // ✅ 일반 수정 모드 (job-detail에서 estimateNo 전달받은 경우)
                        sessionStorage.removeItem(storageKey);

                        const existing = {
                            estimateNo: estimateNoFromState,
                            oneDayMoney: oneDayMoneyFromState || 0
                        };
                        setEditMode(existing);

                        // ✅ API 응답값(detail.oneDayMoney) 우선 사용
                        if (detail.oneDayMoney > 0) {
                            // jobSearchNewDetail.do에서 조회한 최신 시급
                            setProposedRate(detail.oneDayMoney.toLocaleString());
                            console.log('[Apply] API 최신값 사용:', detail.oneDayMoney);
                        } else if (oneDayMoneyFromState) {
                            setProposedRate(oneDayMoneyFromState.toLocaleString());
                            console.log('[Apply] state값 사용:', oneDayMoneyFromState);
                        }

                        if (detail.message) {
                            setMessage(detail.message);
                        }
                    } else {
                        // 신규 지원
                        if (detail.matchType === 'F') {
                            setProposedRate(detail.oneDayMoney.toLocaleString());
                        }

                        if (detail.estimateYn === 1 && detail.estimateNo) {
                            try {
                                const estimateDetail = await jobApi.getEstimateDetail({
                                    memberNo: currentUser.memberNo,
                                    apiKey: currentUser.apiKey,
                                    estimateNo: detail.estimateNo
                                });

                                const existing = {
                                    estimateNo: detail.estimateNo,
                                    oneDayMoney: estimateDetail.oneDayMoney
                                };
                                setEditMode(existing);

                                if (detail.matchType === 'B') {
                                    setProposedRate(estimateDetail.oneDayMoney.toLocaleString());
                                }

                                console.log('[Apply] 기존 견적 정보:', estimateDetail);
                            } catch (error) {
                                console.log('[Apply] 견적 상세 조회 실패:', error);
                            }
                        }
                    }

                } catch (error) {
                    console.error('[Apply] 공고 상세 조회 실패:', error);
                    showAlert('공고 정보를 불러오는데 실패했습니다.', 'error');
                    navigate(fromSentQuotes ? '/profile/sent-quotes' : '/caregiver/job-search');
                }
            }
        } catch (error) {
            console.error('[Apply] 초기 데이터 로드 에러:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFamilyCarePay = async () => {
        if (!proposedRate) {
            showAlert('금액을 입력해주세요.', 'warning');
            return;
        }

        const rate = parseInt(proposedRate.replace(/,/g, ''));
        if (rate < 70000) {
            showAlert('최소 금액은 70,000원입니다.', 'warning');
            return;
        }

        if (!jobId) return;

        setIsSubmitting(true);

        try {
            const currentUser = getCurrentUser();
            if (!currentUser) throw new Error('사용자 정보를 찾을 수 없습니다.');

            let estimateNo: number | undefined;

            const currentIsEditMode = isEditModeRef.current || isEditMode;
            const currentExistingEstimate = existingEstimateRef.current || existingEstimate;

            if (currentIsEditMode && currentExistingEstimate) {
                console.log('[가족간병] 견적 수정 요청:', {
                    estimateNo: currentExistingEstimate.estimateNo,
                    oneDayMoney: rate,
                    careWhat: 2
                });

                await estimateApi.update({
                    memberNo: currentUser.memberNo,
                    apiKey: currentUser.apiKey,
                    estimateNo: currentExistingEstimate.estimateNo,
                    oneDayMoney: rate,
                    careWhat: 2,
                    message: message
                });

                estimateNo = currentExistingEstimate.estimateNo;
                console.log('[가족간병] 견적 수정 성공, estimateNo:', estimateNo);

            } else {
                try {
                    const result = await estimateApi.write({
                        memberNo: currentUser.memberNo,
                        apiKey: currentUser.apiKey,
                        careNo: parseInt(jobId),
                        oneDayMoney: rate,
                        message: message
                    });

                    estimateNo = result.estimateNo;
                    console.log('[가족간병] 견적 작성 성공, estimateNo:', estimateNo);

                } catch (writeError: any) {
                    console.error('[가족간병] 견적 작성 에러:', writeError);

                    if (writeError.code === 201) {
                        showAlert('환급계좌를 등록해주세요.', 'warning', {
                            autoClose: false,
                            onRetry: () => {
                                hideAlert();
                                sessionStorage.setItem('redirectAfterAccount', window.location.pathname);
                                setTimeout(() => navigateTo('/account'), 100);
                            }
                        });
                        return;
                    }

                    if (writeError.code === 220) {
                        const stored = sessionStorage.getItem(storageKey);
                        if (stored) {
                            const data = JSON.parse(stored);
                            if (data.existingEstimate?.estimateNo) {
                                try {
                                    await estimateApi.update({
                                        memberNo: currentUser.memberNo,
                                        apiKey: currentUser.apiKey,
                                        estimateNo: data.existingEstimate.estimateNo,
                                        oneDayMoney: rate,
                                        careWhat: 2,
                                        message: message
                                    });
                                    estimateNo = data.existingEstimate.estimateNo;
                                    setEditMode(data.existingEstimate);
                                    console.log('[가족간병] 220 에러 후 수정 성공, estimateNo:', estimateNo);
                                } catch (updateError) {
                                    throw new Error('견적 수정 중 오류가 발생했습니다.');
                                }
                            } else {
                                throw new Error('견적 번호를 찾을 수 없습니다.');
                            }
                        } else {
                            throw new Error('견적 번호를 찾을 수 없습니다.');
                        }
                    } else {
                        throw writeError;
                    }
                }
            }

            if (!estimateNo) throw new Error('견적 번호를 가져올 수 없습니다.');

            await estimateApi.mdPayCheck({
                memberNo: currentUser.memberNo,
                apiKey: currentUser.apiKey,
                estimateNo: estimateNo
            });

            const stored = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
            stored.existingEstimate = { estimateNo, oneDayMoney: rate };
            stored.careWhat = 2;
            sessionStorage.setItem(storageKey, JSON.stringify(stored));

            navigateTo(`/booking/${jobId}/applicants/${estimateNo}`, {
                isFamilyCare: true
            });

        } catch (error) {
            console.error('[가족간병] 처리 에러:', error);

            if (error instanceof Error) {
                if ((error as any).isAuthExpired) {
                    showAlert('세션이 만료되었습니다. 다시 로그인해주세요.', 'error', {
                        onRetry: () => {
                            sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
                            navigate('/login');
                        }
                    });
                    return;
                }
                showAlert(error.message || '처리 중 오류가 발생했습니다.', 'error');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCaregiverSubmit = async () => {
        if (!jobDetail) return;

        if (!proposedRate) {
            showAlert('견적 금액을 입력해주세요.', 'warning');
            return;
        }

        const rate = parseInt(proposedRate.replace(/,/g, ''));

        if (jobDetail.matchType === 'B') {
            if (jobDetail.careKind === 0 || jobDetail.careKind === 1) {
                if (rate < MIN_HOURLY_WAGE) {
                    showAlert(
                        `시간당 최저임금(${MIN_HOURLY_WAGE.toLocaleString()}원) 이상 입력해주세요.`,
                        'warning'
                    );
                    return;
                }
            } else {
                if (rate < 50000) {
                    showAlert('최소 견적 금액은 50,000원입니다.', 'warning');
                    return;
                }
            }
        }

        if (!jobId) return;

        setIsSubmitting(true);

        try {
            const currentUser = getCurrentUser();
            if (!currentUser) throw new Error('사용자 정보를 찾을 수 없습니다.');

            // ✅ ref 우선 참조
            const currentIsEditMode = isEditModeRef.current || isEditMode;
            const currentExistingEstimate = existingEstimateRef.current || existingEstimate;

            if (currentIsEditMode && currentExistingEstimate) {
                await estimateApi.update({
                    memberNo: currentUser.memberNo,
                    apiKey: currentUser.apiKey,
                    estimateNo: currentExistingEstimate.estimateNo,
                    oneDayMoney: rate,
                    careWhat: careWhatFromState || jobDetail.careWhat || 1,
                    message: message || ''
                });

                console.log('[간병인] 견적 수정 성공');
                sessionStorage.removeItem(storageKey);
                isEditModeRef.current = false;
                existingEstimateRef.current = null;

                showAlert('견적이 수정되었습니다.', 'success', {
                    duration: 2000,
                    onRetry: () => goBack()
                });

            } else {
                await estimateApi.write({
                    memberNo: currentUser.memberNo,
                    apiKey: currentUser.apiKey,
                    careNo: parseInt(jobId),
                    oneDayMoney: rate,
                    careWhat: careWhatFromState || jobDetail.careWhat || 1,
                    message: message || ''
                });

                console.log('[간병인] 견적 작성 성공');
                sessionStorage.removeItem(storageKey);

                showAlert('지원이 완료되었습니다.', 'success', {
                    duration: 2000,
                    onRetry: () => navigateTo('/')
                });
            }

        } catch (error) {
            console.error('[간병인] 처리 에러:', error);

            if (error instanceof Error) {
                if ((error as any).isAuthExpired) {
                    showAlert('로그인이 만료되었습니다.', 'error', {
                        onRetry: () => navigate('/login')
                    });
                    return;
                }

                const errorCode = (error as any).code;

                if (errorCode === 200) {
                    showAlert('성별을 선택해주세요.', 'warning', {
                        autoClose: false,
                        onRetry: () => {
                            hideAlert();
                            setTimeout(() => navigate('/mypage/profile'), 100);
                        }
                    });
                    return;
                }

                if (errorCode === 201) {
                    showAlert('환급계좌를 등록해주세요.', 'warning', {
                        autoClose: false,
                        onRetry: () => {
                            hideAlert();
                            sessionStorage.setItem('redirectAfterAccount', window.location.pathname);
                            setTimeout(() => navigateTo('/account'), 100);
                        }
                    });
                    return;
                }

                if (errorCode === 222) {
                    showAlert('주민번호를 등록해주세요.', 'warning', {
                        autoClose: false,
                        onRetry: () => {
                            hideAlert();
                            setTimeout(() => navigate('/mypage/profile'), 100);
                        }
                    });
                    return;
                }

                showAlert(error.message, 'error');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = () => {
        if (isFamilyCare) {
            handleFamilyCarePay();
        } else {
            handleCaregiverSubmit();
        }
    };

    const handleBackClick = () => {
        sessionStorage.removeItem(storageKey);
        goBack();
    };

    const handleRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        if (value) {
            setProposedRate(parseInt(value).toLocaleString());
        } else {
            setProposedRate('');
        }
    };

    const setSuggestedRate = () => {
        if (jobDetail?.oneDayMoney) {
            setProposedRate(jobDetail.oneDayMoney.toLocaleString());
        }
    };

    const getCareKindLabel = (careKind: number) => {
        switch (careKind) {
            case 0: return '기간제';
            case 1: return '시간제';
            case 2: return '24시간제';
            default: return '기간제';
        }
    };

    const calculateDuration = (startDate: string, endDate: string) => {
        const normalizedStart = startDate.replace(/\./g, '-');
        const normalizedEnd = endDate.replace(/\./g, '-');
        const start = new Date(normalizedStart);
        const end = new Date(normalizedEnd);
        const diffTime = end.getTime() - start.getTime();
        return Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1; // 당일 포함
    };


    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">정보를 불러오는 중...</p>
                </div>
            </div>
        );
    }

    const isFixedPrice = !isFamilyCare && jobDetail?.matchType === 'F';
    // ✅ ref 우선 참조로 버튼/타이틀 표시
    const showEditMode = isEditModeRef.current || isEditMode;

    return (
        <div className="min-h-screen bg-gray-50">
            <TopNavigation
                title={isFamilyCare ? "가족간병비" : (showEditMode ? "견적 수정" : "지원하기")}
                showBackButton
                onBackClick={handleBackClick}
            />

            <div className="pt-16 pb-24">
                {/* 공고 헤더 */}
                {!isFamilyCare && jobDetail && (
                    <div className="p-4 mb-0" style={{ backgroundColor: '#7966FF' }}>
                        {/* 뱃지 행 */}
                        <div className="flex items-center space-x-2 mb-3">
                            <span className="px-2 py-1 bg-white/20 rounded-md text-white text-xs font-medium">
                                {jobDetail.careWhat === 0 ? '단순간병' : jobDetail.careWhat === 1 ? '전문간병' : jobDetail.careWhat === 3 ? '병원동행' : '간병'}
                            </span>
                            <span className="px-2 py-1 bg-white/20 rounded-md text-white text-xs font-medium">
                                {getCareKindLabel(jobDetail.careKind)}
                            </span>
                            {isFixedPrice ? (
                                <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-md font-medium">정찰제</span>
                            ) : (
                                <span className="px-2 py-1 bg-orange-500 text-white text-xs rounded-md font-medium">입찰제</span>
                            )}
                        </div>

                        {/* 환자 이름 */}
                        <div className="mb-3">
                            <h2 className="text-white text-xl font-bold">{jobDetail.ptName}님</h2>
                            <p className="text-white/80 text-sm mt-0.5">
                                {jobDetail.ptAge}세 • {jobDetail.ptGender === 0 ? '남성' : '여성'}
                            </p>
                        </div>

                        {/* 정보 박스 */}
                        <div className="bg-white/10 rounded-xl p-3 space-y-2">
                            <div className="flex items-start space-x-2 text-white/90 text-sm">
                                <i className="ri-map-pin-line flex-shrink-0 mt-0.5"></i>
                                <span className="leading-snug">{jobDetail.careArea}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-white/90 text-sm">
                                <i className="ri-calendar-line flex-shrink-0"></i>
                                <span>{jobDetail.startDate} ~ {jobDetail.endDate}</span>
                                <span className="bg-white/25 text-white text-xs px-1.5 py-0.5 rounded font-medium">
                                    {(() => {
                                        const rawPeriod = jobDetail.careDays
                                            ? jobDetail.careDays.split(',').length
                                            : calculateDuration(jobDetail.startDate, jobDetail.endDate);
                                        return jobDetail.careKind === 2 ? rawPeriod - 1 : rawPeriod;
                                    })()}일
                                </span>
                            </div>
                            {(jobDetail.careKind === 0 || jobDetail.careKind === 1) &&
                                jobDetail.startCareTime && jobDetail.endCareTime && (
                                    <div className="flex items-center space-x-2 text-white/90 text-sm">
                                        <i className="ri-time-line flex-shrink-0"></i>
                                        <span>{jobDetail.startCareTime} ~ {jobDetail.endCareTime}</span>
                                    </div>
                                )}
                        </div>
                    </div>
                )}

                <div className="mt-4 px-4 space-y-3">
                    {/* 견적 금액 카드 */}
                    <Card>
                        <h3 className="font-semibold text-gray-900 mb-4">
                            {isFamilyCare ? '간병 금액' : '견적 금액'}
                        </h3>

                        {/* 금액 입력 */}
                        <div className="mb-3">
                            <label className="block text-xs text-gray-500 mb-1.5">
                                {jobDetail && (jobDetail.careKind === 0 || jobDetail.careKind === 1)
                                    ? '시간당 간병비'
                                    : '일일 간병비'}
                            </label>
                            <div className="relative">
                                {isFixedPrice ? (
                                    <>
                                        <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-lg font-medium text-gray-900">
                                            {proposedRate}
                                        </div>
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">원</span>
                                    </>
                                ) : (
                                    <>
                                        <input
                                            type="text"
                                            value={proposedRate}
                                            onChange={handleRateChange}
                                            placeholder="금액을 입력하세요"
                                            className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-lg font-medium"
                                            style={{ borderColor: proposedRate ? '#7966FF' : undefined }}
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">원</span>
                                    </>
                                )}
                            </div>
                            {/* 기간제/시간제 근무시간 */}
                            {!isFamilyCare && jobDetail &&
                                (jobDetail.careKind === 0 || jobDetail.careKind === 1) &&
                                jobDetail.startCareTime && jobDetail.endCareTime && (() => {
                                    const [sh, sm] = jobDetail.startCareTime.split(':').map(Number);
                                    const [eh, em] = jobDetail.endCareTime.split(':').map(Number);
                                    const hours = Math.round(((eh * 60 + em) - (sh * 60 + sm)) / 60);
                                    return (
                                        <div className="mt-2 flex items-center space-x-1 text-xs text-gray-500">
                                            <i className="ri-time-line text-purple-400"></i>
                                            <span>
                                                근무시간 {jobDetail.startCareTime} ~ {jobDetail.endCareTime}
                                                <span className="ml-1 text-purple-600 font-medium">({hours > 0 ? hours : 8}시간)</span>
                                            </span>
                                        </div>
                                    );
                                })()
                            }
                        </div>

                        {/* 정찰제 안내 */}
                        {isFixedPrice && (
                            <div className="flex items-start gap-2 p-3 rounded-lg mb-3" style={{ background: '#EAF3DE' }}>
                                <i className="ri-check-circle-fill text-sm mt-0.5" style={{ color: '#3B6D11' }}></i>
                                <div>
                                    <p className="text-xs font-medium" style={{ color: '#27500A' }}>정찰제 공고</p>
                                    <p className="text-xs mt-0.5" style={{ color: '#3B6D11' }}>
                                        보호자가 제시한 금액으로 지원됩니다. 금액은 변경할 수 없습니다.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* 보호자 제시 금액 참고 (입찰제 + 신규) */}
                        {!isFamilyCare && !isFixedPrice && jobDetail?.oneDayMoney > 0 && !showEditMode && (
                            <div className="rounded-lg p-3 mb-3" style={{ background: 'var(--color-background-secondary, #f5f5f5)' }}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-gray-500">보호자 제시 금액 (참고)</span>
                                    <span className="text-base font-semibold text-gray-900">{jobDetail.oneDayMoney.toLocaleString()}원</span>
                                </div>
                                <button
                                    onClick={setSuggestedRate}
                                    className="w-full py-2 bg-white border border-gray-200 rounded-lg text-xs text-purple-600 font-medium"
                                >
                                    제시 금액으로 설정
                                </button>
                            </div>
                        )}

                        {/* 가족간병 기간 정보 */}
                        {isFamilyCare && careDetail && (
                            <div className="rounded-lg p-3 mb-3 space-y-2" style={{ background: 'var(--color-background-secondary, #f5f5f5)' }}>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500">간병 기간</span>
                                    <span className="text-gray-900 font-medium">
                                        {careDetail.startDate} ~ {careDetail.endDate}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500">간병 시간</span>
                                    <span className="text-gray-900 font-medium">
                                        {careDetail.startCareTime} ~ {careDetail.endCareTime}
                                    </span>
                                </div>
                                {careDetail.careDays && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500">총 간병일수</span>
                                        <span className="text-gray-900 font-medium">
                                            {(careDetail.careKind === 2 && !isFamilyCare)
                                                ? careDetail.careDays.split(',').length - 1
                                                : careDetail.careDays.split(',').length}일
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* 예상 총금액 계산 박스 */}
                        {proposedRate && !isFamilyCare && jobDetail && (() => {
                            const rate = parseInt(proposedRate.replace(/,/g, '')) || 0;
                            const rawPeriod = jobDetail.careDays
                                ? jobDetail.careDays.split(',').length
                                : calculateDuration(jobDetail.startDate, jobDetail.endDate);
                            const period = jobDetail.careKind === 2 ? rawPeriod - 1 : rawPeriod;

                            const calcHours = () => {
                                if (!jobDetail.startCareTime || !jobDetail.endCareTime) return 8;
                                const [sh, sm] = jobDetail.startCareTime.split(':').map(Number);
                                const [eh, em] = jobDetail.endCareTime.split(':').map(Number);
                                const hours = Math.round(((eh * 60 + em) - (sh * 60 + sm)) / 60);
                                return hours > 0 ? hours : 8;
                            };

                            let estimatedTotal = 0;
                            let dailyHours = 0;

                            if (jobDetail.careKind === 0 || jobDetail.careKind === 1) {
                                dailyHours = calcHours();
                                estimatedTotal = rate * dailyHours * period;
                            } else {
                                estimatedTotal = rate * period;
                            }

                            return (
                                <div className="rounded-lg p-3 space-y-2" style={{ background: 'var(--color-background-secondary, #f5f5f5)' }}>
                                    {/* 기간제/시간제: 시급 → 하루 간병비 */}
                                    {(jobDetail.careKind === 0 || jobDetail.careKind === 1) && (
                                        <div className="flex justify-between text-xs text-gray-500">
                                            <span>하루 간병비 ({dailyHours}시간)</span>
                                            <span className="text-gray-700">{(rate * dailyHours).toLocaleString()}원</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-xs text-gray-500">
                                        <span>간병 일수</span>
                                        <span className="text-gray-700">{period}일</span>
                                    </div>
                                    <div className="border-t border-gray-200 pt-2 flex items-baseline justify-between">
                                        <span className="text-sm font-medium text-gray-700">예상 총 간병비</span>
                                        <span className="text-lg font-semibold" style={{ color: '#854F0B' }}>
                                            {estimatedTotal.toLocaleString()}원
                                        </span>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* 수정 모드 안내 */}
                        {!isFamilyCare && showEditMode && (
                            <div className="flex items-start gap-2 p-3 rounded-lg mt-3" style={{ background: '#FAEEDA' }}>
                                <i className="ri-edit-line text-sm mt-0.5" style={{ color: '#854F0B' }}></i>
                                <p className="text-xs" style={{ color: '#854F0B' }}>
                                    이전에 제출한 견적을 수정 중입니다. 새 금액 입력 후 하단 버튼을 눌러주세요.
                                </p>
                            </div>
                        )}

                        {/* 가족간병 수정 모드 안내 */}
                        {isFamilyCare && showEditMode && (
                            <div className="flex items-start gap-2 p-3 rounded-lg mt-3" style={{ background: '#FAEEDA' }}>
                                <i className="ri-edit-line text-sm mt-0.5" style={{ color: '#854F0B' }}></i>
                                <p className="text-xs" style={{ color: '#854F0B' }}>
                                    이전에 입력한 금액을 수정 중입니다. 새 금액을 입력 후 결제 확인하기를 누르면 금액이 업데이트됩니다.
                                </p>
                            </div>
                        )}
                    </Card>

                    {/* 지원 메시지 카드 */}
                    {!isFamilyCare && (
                        <Card>
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold text-gray-900">지원 메시지</h3>
                                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">선택</span>
                            </div>
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="보호자에게 전달할 메시지를 입력하세요&#10;예) 경력 10년으로 성심껏 돌봐드리겠습니다."
                                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none text-gray-800"
                                rows={4}
                                maxLength={200}
                            />
                            <div className="text-right text-xs text-gray-400 mt-1.5">
                                {message.length}/200
                            </div>
                        </Card>
                    )}

                    {/* 지원/결제 안내 카드 */}
                    <div className="rounded-xl p-4 bg-orange-50 border border-orange-100">
                        <div className="flex items-center gap-2 mb-2">
                            <i className="ri-information-line text-orange-500 text-sm"></i>
                            <h4 className="text-sm font-medium text-orange-900">
                                {isFamilyCare ? '결제 안내' : '지원 안내'}
                            </h4>
                        </div>
                        <ul className="space-y-1.5">
                            {isFamilyCare ? (
                                <>
                                    <li className="flex gap-1.5 text-xs text-orange-700"><span>•</span><span>입력하신 금액을 확인 후 결제가 진행됩니다</span></li>
                                    <li className="flex gap-1.5 text-xs text-orange-700"><span>•</span><span>간병 완료 후 관련 증빙 자료가 생성됩니다</span></li>
                                    <li className="flex gap-1.5 text-xs text-orange-700"><span>•</span><span>보험서류 메뉴에서 다운로드할 수 있습니다</span></li>
                                </>
                            ) : isFixedPrice ? (
                                <>
                                    <li className="flex gap-1.5 text-xs text-orange-700"><span>•</span><span>정찰제 공고로 보호자 제시 금액으로 지원됩니다</span></li>
                                    <li className="flex gap-1.5 text-xs text-orange-700"><span>•</span><span>지원 완료 후 보호자가 선택하면 알림을 드립니다</span></li>
                                    <li className="flex gap-1.5 text-xs text-orange-700"><span>•</span><span>최종 선정 시 간병이 시작됩니다</span></li>
                                </>
                            ) : (
                                <>
                                    <li className="flex gap-1.5 text-xs text-orange-700">
                                        <span>•</span>
                                        <span>견적 금액은 {jobDetail && (jobDetail.careKind === 0 || jobDetail.careKind === 1) ? '시간당 간병비' : '일일 간병비'}입니다</span>
                                    </li>
                                    <li className="flex gap-1.5 text-xs text-orange-700"><span>•</span><span>선정된 견적은 자동으로 알림을 드립니다</span></li>
                                    <li className="flex gap-1.5 text-xs text-orange-700"><span>•</span><span>최종 견적 금액은 마감 전까지 수정 가능합니다</span></li>
                                </>
                            )}
                        </ul>
                    </div>
                </div>
            </div>

            {/* 하단 고정 버튼 */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
                <Button
                    className="w-full text-white disabled:bg-gray-400"
                    style={{ backgroundColor: (!proposedRate || isSubmitting) ? undefined : '#7966FF' }}
                    onClick={handleSubmit}
                    disabled={!proposedRate || isSubmitting}
                >
                    {isSubmitting ? (
                        <>처리 중...</>
                    ) : isFamilyCare ? (
                        <><i className="ri-arrow-right-line mr-2"></i>{showEditMode ? '금액 수정 후 결제 확인' : '결제 확인하기'}</>
                    ) : showEditMode ? (
                        <><i className="ri-edit-line mr-2"></i>견적 수정</>
                    ) : (
                        <><i className="ri-send-plane-line mr-2"></i>지원 완료</>
                    )}
                </Button>
            </div>
        </div>
    );
}